import { useQuery } from "@tanstack/react-query";
import useRetryFetch from "./useRetryFetch";
import useAuth from "./useAuth";
import { config } from "../Environment";
import { getStats } from "../api/userTakeaways";
import { getMyReminders } from "../api/checkpointReminders";

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const PORTAL_ORGANIZATIONS_KEY = ["portal", "organizations"] as const;
export const MENTOR_COLLECTIONS_KEY = ["portal", "mentor-collections"] as const;
export const WORKSPACE_STATS_KEY = ["portal", "workspace-stats"] as const;

// ─── Organizations Query ────────────────────────────────────────────────────

/**
 * Fetches organizations and determines which ones the user is an admin of.
 * Shared between Portal (for tab gating) and MyGroups (for org overview card).
 */
export function useOrganizationsQuery() {
  const retryFetch = useRetryFetch();
  const { user } = useAuth();

  return useQuery<any[]>({
    queryKey: [...PORTAL_ORGANIZATIONS_KEY, user?._id],
    queryFn: async () => {
      const response = await retryFetch(
        `${config.API_BASE}/api/organizations`
      );
      if (!response.ok) return [];
      const orgs = await response.json();
      return orgs;
    },
    enabled: !!user?._id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Derives org admin status from the organizations query.
 */
export function useOrgAdminStatus() {
  const { data: organizations = [] } = useOrganizationsQuery();
  const { user } = useAuth();

  const adminOrgs = organizations.filter((org: any) =>
    org.fiOrgAdmins?.some(
      (admin: any) =>
        (admin._id || admin).toString() === user?._id?.toString()
    )
  );

  return {
    isOrgAdmin: adminOrgs.length > 0,
    orgAdminOrgs: adminOrgs,
    allOrganizations: organizations,
  };
}

// ─── Mentor Collections Query ───────────────────────────────────────────────

interface MentorCollectionsResponse {
  collections: any[];
}

/**
 * Fetches mentor's assigned collections.
 * Shared between Portal (for tab count gating) and MyGroups (for the full list).
 */
export function useMentorCollectionsQuery(enabled = true) {
  const retryFetch = useRetryFetch();
  const { user } = useAuth();

  return useQuery<MentorCollectionsResponse>({
    queryKey: [...MENTOR_COLLECTIONS_KEY, user?._id],
    queryFn: async () => {
      const response = await retryFetch(
        `${config.API_BASE}/api/mentor/collections`
      );
      if (!response.ok) return { collections: [] };
      return await response.json();
    },
    enabled: !!user?._id && enabled,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// ─── Workspace Stats Query ──────────────────────────────────────────────────

interface WorkspaceStats {
  pendingActions: number;
  totalInsights: number;
  upcomingReminders: number;
}

/**
 * Fetches workspace badge counts (action items, insights, reminders).
 * Used by MyWorkspace for sidebar/tab badge counts.
 */
export function useWorkspaceStatsQuery() {
  const retryFetch = useRetryFetch();

  return useQuery<WorkspaceStats>({
    queryKey: WORKSPACE_STATS_KEY,
    queryFn: async () => {
      const [statsResponse, remindersResponse] = await Promise.all([
        getStats(retryFetch),
        getMyReminders(retryFetch, {
          status: "pending",
          includeArchived: false,
        }).catch(() => ({ reminders: [] })),
      ]);

      return {
        pendingActions:
          statsResponse.status === "STATS"
            ? statsResponse.stats.pendingActions
            : 0,
        totalInsights:
          statsResponse.status === "STATS"
            ? statsResponse.stats.totalInsights
            : 0,
        upcomingReminders: remindersResponse.reminders?.length || 0,
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
